import { Emitter } from "../utils/Emitter";
import type { Quality, QualitySettings } from "../types/quality";

export const QUALITY_LEVELS: readonly Quality[] = ["low", "medium", "high"];

export const QUALITY_PRESETS: Record<Quality, QualitySettings> = {
  low: {
    maxPixelRatio: 1,
    shadows: false,
    shadowMapSize: 512,
    maxDynamicLights: 1,
    antialias: false,
    postProcessing: false,
    anisotropy: 1,
  },
  medium: {
    maxPixelRatio: 1.5,
    shadows: true,
    shadowMapSize: 1024,
    maxDynamicLights: 2,
    antialias: false,
    postProcessing: false,
    anisotropy: 4,
  },
  high: {
    maxPixelRatio: 2,
    shadows: true,
    shadowMapSize: 2048,
    maxDynamicLights: 4,
    antialias: true,
    postProcessing: true,
    anisotropy: 8,
  },
};

export interface QualityEvents extends Record<string, unknown> {
  change: { level: Quality; settings: QualitySettings };
}

/**
 * Source de vérité du niveau de détail. Le nom `Quality` étant pris par le
 * type, la classe s'appelle QualityManager (seule entorse à « fichier = classe »).
 */
export class QualityManager {
  readonly events = new Emitter<QualityEvents>();

  private currentLevel: Quality;
  private currentSettings: QualitySettings;

  /** `pixelRatioCap` vient de Device : le matériel a le dernier mot sur le preset. */
  constructor(
    level: Quality,
    private readonly pixelRatioCap: number,
  ) {
    this.currentLevel = level;
    this.currentSettings = this.resolve(level);
  }

  get level(): Quality {
    return this.currentLevel;
  }

  get settings(): QualitySettings {
    return this.currentSettings;
  }

  set(level: Quality): void {
    if (level === this.currentLevel) return;
    this.currentLevel = level;
    this.currentSettings = this.resolve(level);
    this.events.emit("change", { level, settings: this.currentSettings });
  }

  private resolve(level: Quality): QualitySettings {
    const preset = QUALITY_PRESETS[level];
    return {
      ...preset,
      maxPixelRatio: Math.min(preset.maxPixelRatio, this.pixelRatioCap),
    };
  }

  dispose(): void {
    this.events.dispose();
  }
}
